import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  MenuItem,
  Chip,
  Alert,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Person as PersonIcon,
  Straighten as RulerIcon,
} from '@mui/icons-material';

// Standard size presets (body measurements)
const SIZE_PRESETS = {
  XS: {
    bust: 85, waist: 67, hip: 90, shoulder: 12, neck: 36, bicep: 28,
    armhole_depth: 24, front_waist_len: 47, back_waist_len: 48,
    waist_to_hem: 26, shoulder_slope_deg: 12,
    bust_point_height: 25, back_width: 34, arm_length: 56
  },
  S: {
    bust: 91, waist: 73, hip: 96, shoulder: 13, neck: 37, bicep: 30,
    armhole_depth: 25, front_waist_len: 48, back_waist_len: 49,
    waist_to_hem: 26, shoulder_slope_deg: 12,
    bust_point_height: 26, back_width: 36, arm_length: 58
  },
  M: {
    bust: 97, waist: 79, hip: 102, shoulder: 14, neck: 38, bicep: 33,
    armhole_depth: 26, front_waist_len: 49, back_waist_len: 50,
    waist_to_hem: 26, shoulder_slope_deg: 12,
    bust_point_height: 27, back_width: 38, arm_length: 60
  },
  L: {
    bust: 103, waist: 85, hip: 108, shoulder: 15, neck: 39, bicep: 36,
    armhole_depth: 27, front_waist_len: 50, back_waist_len: 51,
    waist_to_hem: 26, shoulder_slope_deg: 12,
    bust_point_height: 28, back_width: 40, arm_length: 61
  },
  XL: {
    bust: 109, waist: 91, hip: 114, shoulder: 16, neck: 40, bicep: 38,
    armhole_depth: 28, front_waist_len: 51, back_waist_len: 52,
    waist_to_hem: 26, shoulder_slope_deg: 12,
    bust_point_height: 29, back_width: 42, arm_length: 62
  },
};

const ModelSizingInput = ({ patternBlock, modelSizing, onUpdateSizing }) => {
  const [selectedSize, setSelectedSize] = useState('');
  const [customMeasurements, setCustomMeasurements] = useState({});

  useEffect(() => {
    if (modelSizing && Object.keys(modelSizing).length > 0) {
      setCustomMeasurements(modelSizing);
    }
  }, [modelSizing]);

  const handleSizePresetSelect = (size) => {
    setSelectedSize(size);
    const preset = SIZE_PRESETS[size];

    const measurements = {
      neck_circ: preset.neck,
      bust_circ: preset.bust,
      waist_circ: preset.waist,
      hip_circ: preset.hip,
      bicep_circ: preset.bicep,
      shoulder_len: preset.shoulder,
      armhole_depth: preset.armhole_depth,
      front_waist_len: preset.front_waist_len,
      back_waist_len: preset.back_waist_len,
      waist_to_hem: preset.waist_to_hem,
      shoulder_slope_deg: preset.shoulder_slope_deg,
      bust_point_height: preset.bust_point_height,
      back_width: preset.back_width,
      arm_length: preset.arm_length,
    };

    setCustomMeasurements(measurements);
    onUpdateSizing(measurements);
  };

  const handleCustomMeasurementChange = (field, value) => {
    const numValue = value === '' ? '' : parseFloat(value);
    const updatedMeasurements = {
      ...customMeasurements,
      [field]: numValue,
    };

    setCustomMeasurements(updatedMeasurements);
    onUpdateSizing(updatedMeasurements);
    setSelectedSize(''); // Clear preset selection when customizing
  };

  const requiredMeasurements = patternBlock?.measurements || [
    'neck_circ',
    'bust_circ',
    'waist_circ',
    'shoulder_len',
    'armhole_depth',
    'front_waist_len',
  ];

  // Define measurement groups and labels
  const measurementGroups = {
    circumference: ['neck_circ', 'bust_circ', 'waist_circ', 'hip_circ', 'bicep_circ'],
    length: ['shoulder_len', 'armhole_depth', 'front_waist_len', 'back_waist_len', 'waist_to_hem', 'arm_length', 'bust_point_height', 'back_width'],
    other: ['shoulder_slope_deg'],
  };

  const measurementLabels = {
    neck_circ: 'Neck Circumference',
    bust_circ: 'Bust Circumference',
    waist_circ: 'Waist Circumference',
    hip_circ: 'Hip Circumference',
    bicep_circ: 'Bicep Circumference',
    shoulder_len: 'Shoulder Length',
    armhole_depth: 'Armhole Depth',
    front_waist_len: 'Front Waist Length',
    back_waist_len: 'Back Waist Length',
    waist_to_hem: 'Waist to Hem',
    shoulder_slope_deg: 'Shoulder Slope',
    arm_length: 'Arm Length (shoulder to wrist)',
    bust_point_height: 'Bust Point Height',
    back_width: 'Back Width',
  };

  const measurementUnits = {
    shoulder_slope_deg: '°',
    // All others default to 'cm'
  };

  // Get all measurements to display (combine required + common ones)
  const allMeasurements = [...new Set([
    ...requiredMeasurements,
    ...Object.values(measurementGroups).flat(),
  ])];

  // Group measurements for display
  const groupedMeasurements = {
    circumference: allMeasurements.filter(m => measurementGroups.circumference.includes(m)),
    length: allMeasurements.filter(m => measurementGroups.length.includes(m)),
    other: allMeasurements.filter(m =>
      !measurementGroups.circumference.includes(m) &&
      !measurementGroups.length.includes(m)
    ),
  };

  const isComplete = requiredMeasurements.every(
    (m) => customMeasurements[m] && customMeasurements[m] > 0
  );

  const renderMeasurementField = (measurement) => {
    const label = measurementLabels[measurement] || measurement.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    const unit = measurementUnits[measurement] || 'cm';
    const isRequired = requiredMeasurements.includes(measurement);

    return (
      <Grid item xs={12} sm={6} md={4} key={measurement}>
        <TextField
          fullWidth
          label={label}
          type="number"
          value={customMeasurements[measurement] || ''}
          onChange={(e) => handleCustomMeasurementChange(measurement, e.target.value)}
          InputProps={{
            endAdornment: <InputAdornment position="end">{unit}</InputAdornment>,
          }}
          size="small"
          required={isRequired}
        />
      </Grid>
    );
  };

  return (
    <Box>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        Input Model Sizing
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Provide body measurements for pattern generation. You can start with a standard size preset or enter custom measurements.
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Note:</strong> These are body measurements. The actual garment sleeve length (short, long, or sleeveless) will be automatically calculated based on the selected pattern type and your arm length.
        </Typography>
      </Alert>

      {/* Size Presets */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <PersonIcon color="primary" />
            <Typography variant="subtitle1" fontWeight={600}>
              Quick Start: Size Presets
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {Object.keys(SIZE_PRESETS).map((size) => (
              <Button
                key={size}
                variant={selectedSize === size ? 'contained' : 'outlined'}
                onClick={() => handleSizePresetSelect(size)}
                sx={{ minWidth: 80 }}
              >
                {size}
              </Button>
            ))}
          </Box>

          {selectedSize && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                Size <strong>{selectedSize}</strong> loaded. You can adjust individual measurements below.
              </Typography>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Custom Measurements */}
      <Card variant="outlined">
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <RulerIcon color="primary" />
            <Typography variant="subtitle1" fontWeight={600}>
              Body Measurements (cm)
            </Typography>
          </Box>

          <Grid container spacing={2}>
            {/* Circumference Measurements */}
            {groupedMeasurements.circumference.length > 0 && (
              <>
                <Grid item xs={12}>
                  <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 1 }}>
                    Circumference Measurements
                  </Typography>
                </Grid>
                {groupedMeasurements.circumference.map(renderMeasurementField)}
              </>
            )}

            {/* Length Measurements */}
            {groupedMeasurements.length.length > 0 && (
              <>
                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 1 }}>
                    Length & Construction Measurements
                  </Typography>
                </Grid>
                {groupedMeasurements.length.map(renderMeasurementField)}
              </>
            )}

            {/* Other Measurements */}
            {groupedMeasurements.other.length > 0 && (
              <>
                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 1 }}>
                    Additional Measurements
                  </Typography>
                </Grid>
                {groupedMeasurements.other.map(renderMeasurementField)}
              </>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Measurement Summary */}
      <Paper variant="outlined" sx={{ mt: 3, p: 2 }}>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Measurement Summary
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Measurement</TableCell>
                <TableCell align="right">Value</TableCell>
                <TableCell align="center">Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requiredMeasurements.map((measurement) => {
                const value = customMeasurements[measurement];
                const isSet = value && value > 0;
                return (
                  <TableRow key={measurement}>
                    <TableCell>{measurement.replace(/_/g, ' ').toUpperCase()}</TableCell>
                    <TableCell align="right">
                      {isSet ? `${value} cm` : '-'}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={isSet ? 'Set' : 'Required'}
                        size="small"
                        color={isSet ? 'success' : 'warning'}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Completion Alert */}
      {isComplete && (
        <Alert severity="success" sx={{ mt: 2 }}>
          <Typography variant="body2">
            All required measurements complete! Click "Generate Pattern" to create your pattern.
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default ModelSizingInput;
